import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import {
  BellIcon,
  Button,
  Card,
  EmptyState,
  TabBar,
} from '../components/ui';
import { WhaleLoader } from '../components/WhaleLoader';
import { useInfiniteScrollSentinel } from '../hooks/useInfiniteScrollSentinel';
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
} from '../hooks/useNotifications';
import { formatResumeTime, formatUsd } from '../lib/format';
import { sideLabel, strategyLabel } from '../lib/strategyLabel';
import type { NotificationItem } from '../lib/types';

type Filter = 'all' | 'unread';

/** Cancellation reason codes mirrored from `live_order` / `hl/executor`.
 * Keep in sync with `notifications.cancel_reason.*` in `i18n/*.json`. */
const TRADE_CANCEL_REASON_KEYS = new Set([
  'not_matched',
  'no_match',
  'pending_order_timeout',
  'post_rejected',
  'manual',
]);

export function Notifications() {
  const { t, i18n } = useTranslation();
  const [filter, setFilter] = useState<Filter>('all');
  const q = useNotifications(filter === 'unread');
  const markRead = useMarkNotificationRead();
  const markAll = useMarkAllNotificationsRead();

  const items = useMemo(
    () => q.data?.pages.flatMap((p) => p.items) ?? [],
    [q.data],
  );
  const locale = i18n.language.startsWith('ru') ? 'ru-RU' : 'en-US';

  const sentinelRef = useInfiniteScrollSentinel({
    onLoadMore: () => q.fetchNextPage(),
    enabled: !!q.hasNextPage && !q.isFetchingNextPage && !q.isLoading,
  });

  const hasUnread = items.some((it) => !it.read_at);

  return (
    <div className="mx-auto max-w-app space-y-4 px-5 pt-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="pb-display text-[28px] leading-tight">
            {t('notifications.title')}
          </h1>
          <p className="mt-1.5 text-[14px] leading-relaxed text-tg-hint">
            {t('notifications.subtitle')}
          </p>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3">
        <TabBar
          value={filter}
          size="sm"
          onChange={setFilter}
          options={[
            { value: 'all' as const, label: t('notifications.tab_all') },
            { value: 'unread' as const, label: t('notifications.tab_unread') },
          ]}
        />
        <Button
          variant="secondary"
          size="sm"
          onClick={() => markAll.mutate()}
          disabled={markAll.isPending || !hasUnread}
        >
          {t('notifications.mark_all_read')}
        </Button>
      </div>

      {q.isLoading ? (
        <Card pad="lg" className="flex justify-center">
          <WhaleLoader />
        </Card>
      ) : items.length === 0 ? (
        <EmptyState
          icon={<BellIcon size={22} />}
          title={t('notifications.empty_title')}
          body={t('notifications.empty_body')}
        />
      ) : (
        <Card pad="none" variant="flat">
          <ul className="divide-y divide-pb-border-soft">
            {items.map((it) => (
              <li key={it.id}>
                <NotificationRow
                  item={it}
                  locale={locale}
                  onRead={() =>
                    !it.read_at &&
                    markRead.mutate({ notificationId: it.id })
                  }
                />
              </li>
            ))}
          </ul>
        </Card>
      )}

      {q.isFetchingNextPage ? (
        <div className="mt-3 flex justify-center">
          <WhaleLoader />
        </div>
      ) : null}

      <div ref={sentinelRef} className="h-px w-full" aria-hidden />

      {!q.hasNextPage && items.length > 0 ? (
        <p className="pb-caption mt-4 text-center">
          {t('notifications.end_of_list')}
        </p>
      ) : null}
    </div>
  );
}

function NotificationRow({
  item,
  locale,
  onRead,
}: {
  item: NotificationItem;
  locale: string;
  onRead: () => void;
}) {
  const { t } = useTranslation();
  const dt = new Date(item.created_at);
  const dateLabel = dt.toLocaleDateString(locale, {
    day: '2-digit',
    month: 'short',
  });
  const timeLabel = dt.toLocaleTimeString(locale, {
    hour: '2-digit',
    minute: '2-digit',
  });
  const tone = toneFor(item.kind, item.payload as Record<string, unknown>);
  const summary = summaryFor(item, t, locale);

  return (
    <button
      type="button"
      onClick={onRead}
      className={clsx(
        'pb-focus-ring grid w-full grid-cols-[10px_1fr] items-start gap-3 px-4 py-3.5 text-left transition-colors',
        !item.read_at && 'bg-pb-accent-soft/20',
        'hover:bg-pb-paper2',
      )}
    >
      <span
        aria-hidden
        className={clsx(
          'mt-1.5 inline-block h-2 w-2 shrink-0 rounded-full',
          item.read_at ? 'bg-transparent' : 'bg-pb-accent',
        )}
      />
      <div className="min-w-0">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <span
            className={clsx(
              'text-[14px] font-medium',
              tone === 'success' && 'text-pb-success',
              tone === 'warn' && 'text-pb-warn',
              tone === 'danger' && 'text-pb-danger',
              tone === 'neutral' && 'text-tg-text',
            )}
          >
            {summary.title}
          </span>
        </div>
        <p className="pb-caption mt-0.5 break-words leading-snug">
          {dateLabel} · {timeLabel}
          {summary.body ? ` · ${summary.body}` : ''}
        </p>
      </div>
    </button>
  );
}

export function toneFor(
  kind: string,
  payload?: Record<string, unknown>,
): 'success' | 'warn' | 'danger' | 'neutral' {
  if (kind === 'fee_charged' || kind === 'config_changed') return 'neutral';
  if (kind === 'withdrawal_decision') return 'success';
  if (kind === 'kill_switch') return 'warn';
  if (kind === 'deposit.confirmed') return 'success';
  if (kind === 'withdrawal.completed') return 'success';
  if (kind === 'withdrawal.failed') return 'danger';
  if (kind === 'trade.opened') return 'neutral';
  if (kind === 'trade.cancelled') return 'warn';
  if (kind === 'pm_trading_paused') return 'warn';
  if (kind === 'trade.settled') {
    return payload?.win === true ? 'success' : 'danger';
  }
  return 'neutral';
}

export function summaryFor(
  item: NotificationItem,
  t: (k: string, opts?: Record<string, unknown>) => string,
  locale = 'en-US',
): { title: string; body: string | null } {
  const p = item.payload as Record<string, unknown>;
  switch (item.kind) {
    case 'fee_charged': {
      const amount = formatUsd((p.amount_usd as string) ?? '0');
      return {
        title: t('notifications.kind.fee_charged.title'),
        body: t('notifications.kind.fee_charged.body', { amount }),
      };
    }
    case 'withdrawal_decision': {
      const decision = p.decision as string | undefined;
      if (decision === 'rejected') {
        const reason = (p.reason as string | null) ?? '';
        return {
          title: t('notifications.kind.withdrawal_rejected.title'),
          body: reason || null,
        };
      }
      return {
        title: t('notifications.kind.withdrawal_approved.title'),
        body: null,
      };
    }
    case 'kill_switch':
      return {
        title: t('notifications.kind.kill_switch.title'),
        body: null,
      };
    case 'config_changed':
      return {
        title: t('notifications.kind.config_changed.title'),
        body: null,
      };
    case 'trade.opened': {
      const strategy = strategyLabel(
        p.strategy as string | null,
        p.setup as string | null,
        t,
      );
      const side = sideLabel(p.side as string | null, t);
      const notional = formatUsd((p.notional_usdc as string) ?? '0');
      return {
        title: t('notifications.kind.trade_opened.title'),
        body: t('notifications.kind.trade_opened.body', { strategy, side, notional }),
      };
    }
    case 'trade.settled': {
      const won = p.win === true;
      const amount = formatUsd((p.realized_pnl_usdc as string) ?? '0');
      return won
        ? {
            title: t('notifications.kind.trade_settled_won.title'),
            body: t('notifications.kind.trade_settled_won.body', { amount }),
          }
        : {
            title: t('notifications.kind.trade_settled_lost.title'),
            body: t('notifications.kind.trade_settled_lost.body', { amount }),
          };
    }
    case 'trade.cancelled': {
      // `reason` is an internal enum (`not_matched`, `no_match`,
      // `pending_order_timeout`, `post_rejected`, `manual`, …) emitted
      // by `live_order`/`hl/executor`. Map known codes to localized
      // copy; unknown codes fall back to a generic phrase rather than
      // leak the raw token to the user.
      const code = (p.reason as string) ?? '';
      const key =
        code && TRADE_CANCEL_REASON_KEYS.has(code)
          ? `notifications.cancel_reason.${code}`
          : 'notifications.cancel_reason.unknown';
      return {
        title: t('notifications.kind.trade_cancelled.title'),
        body: t(key),
      };
    }
    case 'pm_trading_paused': {
      // Auto circuit-breaker (per-user daily-loss stop). Payload:
      // { reason, pnl_24h, limit_pct, resume_at }. Show the figures
      // that tripped it + when it auto-resumes.
      const resume = formatResumeTime(p.resume_at as string | undefined, locale);
      // pnl_24h is the signed 24h PnL (negative for a loss); show its
      // magnitude — the copy already says "loss limit reached".
      const loss = formatUsd(Math.abs(Number(p.pnl_24h ?? 0)));
      const limitPct = p.limit_pct != null ? String(p.limit_pct) : null;
      const detail = limitPct
        ? t('notifications.kind.pm_trading_paused.body_daily_loss', {
            loss,
            limit: limitPct,
          })
        : t('notifications.kind.pm_trading_paused.body_generic');
      const body = resume
        ? `${detail} ${t('notifications.kind.pm_trading_paused.resumes', { time: resume })}`
        : detail;
      return {
        title: t('notifications.kind.pm_trading_paused.title'),
        body,
      };
    }
    case 'deposit.confirmed': {
      const amount = formatUsd((p.amount_usd as string) ?? '0');
      return {
        title: t('notifications.kind.deposit_confirmed.title'),
        body: t('notifications.kind.deposit_confirmed.body', { amount }),
      };
    }
    case 'withdrawal.completed': {
      const amount = formatUsd((p.amount_usd as string) ?? '0');
      return {
        title: t('notifications.kind.withdrawal_completed.title'),
        body: t('notifications.kind.withdrawal_completed.body', { amount }),
      };
    }
    case 'withdrawal.failed': {
      const error = (p.error as string) ?? '';
      return {
        title: t('notifications.kind.withdrawal_failed.title'),
        body: error || null,
      };
    }
    default:
      return {
        title: t('notifications.kind.fallback', { kind: item.kind }),
        body: null,
      };
  }
}
